// Idle-session policy for the clinician admin.
//
// ── WHY THIS EXISTS, and what it deliberately does NOT do ────────────────────
//
// The server already caps a clinician session ABSOLUTELY: the JWT carries `exp`
// from TOKEN_EXPITATION_USER (`5h` on prod2, verified live 2026-10-14) and
// src/business/auth_middleware.js rejects an expired token on every request.
// That bound is the important one, and this module does not touch it:
//
//   * it is server-enforced, so it holds against anyone talking to the GraphQL
//     API directly, which is what bounds a STOLEN token; and
//   * it is the only thing that makes a role change or a deactivation take
//     effect. `current_user` IS the decoded token (auth_middleware.js:46) --
//     role, customer and surgeon are baked in at login and nothing re-reads the
//     user row mid-session. A revoked clinician keeps their old access until
//     that token expires.
//
// What an absolute cap cannot do is notice that nobody is at the keyboard. A
// clinician who walks away leaves a patient record on screen for up to the full
// 5h. Closing THAT window is the only job of this module, and it is the HIPAA
// automatic-logoff case -- 45 CFR 164.312(a)(2)(iii), an addressable
// implementation specification.
//
// ⚠️ So this is a physical-security control, not an access control. It runs in
// the browser; anyone holding the token can ignore it completely. Do not cite
// it as a safeguard against token theft, and do not let it become an argument
// for lengthening the 5h -- the two are not interchangeable.
//
// ── Why the logic lives here, away from React ────────────────────────────────
// Everything below is pure and takes `now` as an argument, so the policy can be
// tested without a DOM, a clock or a rendered component (see
// idletimeout.test.js). The component is only wiring.

export const PHASE = {
  ACTIVE: 'active',
  WARNING: 'warning',
  EXPIRED: 'expired',
}

// Shared across tabs ON PURPOSE. sessionStorage is per-tab, so a clinician with
// the admin open twice would be logged out of an idle tab while actively
// working in the other one. localStorage is the only storage both tabs see.
export const ACTIVITY_KEY = 'moveai.admin.lastActivity'

/**
 * Coerce a duration that may have arrived as a string.
 *
 * env.sh builds env-config.js by echoing `NAME: "value"` for every line of
 * .env, so EVERY runtime value reaches the app as a STRING -- `"1800000"`, not
 * 1800000. A missing variable is simply absent from the file, and a variable
 * present but empty arrives as "". Both must fall back rather than produce
 * NaN, which would make setTimeout fire immediately and log everyone out on
 * sight.
 */
export function parseDurationMs(raw, fallbackMs) {
  if (raw === undefined || raw === null || raw === '') return fallbackMs
  const n = typeof raw === 'number' ? raw : Number(String(raw).trim())
  if (!Number.isFinite(n) || n <= 0) return fallbackMs
  return n
}

/**
 * How long before logout the warning appears.
 *
 * Clamped to at most half the idle window: a misconfigured warning longer than
 * the timeout itself would leave the session permanently in WARNING, showing a
 * modal the moment anyone logs in.
 */
export function effectiveWarningMs(timeoutMs, warningMs) {
  const half = Math.floor(timeoutMs / 2)
  if (!Number.isFinite(warningMs) || warningMs <= 0) return 0
  return Math.min(warningMs, half)
}

/**
 * The whole policy, as a pure function.
 *
 * @param {number} now           epoch ms
 * @param {number} lastActivity  epoch ms of the last recorded interaction
 * @param {number} timeoutMs     idle window
 * @param {number} warningMs     how long before logout to warn
 * @returns {{phase: string, msUntilLogout: number, idleMs: number}}
 */
export function idleState({ now, lastActivity, timeoutMs, warningMs }) {
  // An absent or junk timestamp is treated as "active, starting now" rather
  // than as expired. The component records activity on mount, so in practice
  // the key always exists; a first tick that raced the write must not throw a
  // clinician out of a session they just started.
  let last = Number(lastActivity)
  if (!Number.isFinite(last) || last <= 0) last = now
  // A timestamp in the future means clock skew, or another tab writing from a
  // machine whose clock differs. Clamp rather than return a negative idle time.
  if (last > now) last = now

  const idleMs = now - last
  const msUntilLogout = timeoutMs - idleMs
  const warn = effectiveWarningMs(timeoutMs, warningMs)

  if (msUntilLogout <= 0) {
    return { phase: PHASE.EXPIRED, msUntilLogout: 0, idleMs }
  }
  if (msUntilLogout <= warn) {
    return { phase: PHASE.WARNING, msUntilLogout, idleMs }
  }
  return { phase: PHASE.ACTIVE, msUntilLogout, idleMs }
}

/**
 * Should a passive browser event (mousemove, scroll, keypress...) count as
 * activity and push the deadline back?
 *
 * Yes while ACTIVE -- normal work should never hit the timeout.
 *
 * No once WARNING has been reached, which is the deliberate part. By then
 * nobody has interacted for the whole idle window, and the screen is exactly
 * the unattended one this control exists for. A passer-by nudging the desk, a
 * cat on the keyboard, or a page's own scroll restoration must not silently
 * renew access to a patient record. Past that line we require the explicit
 * "Stay signed in" click.
 *
 * This costs nothing in normal use: reaching WARNING already means the full
 * idle window elapsed with no interaction, so a clinician who is actually
 * working never sees it.
 */
export function passiveActivityRenews(phase) {
  return phase === PHASE.ACTIVE
}
